import { createError, defineNuxtRouteMiddleware } from 'nuxt/app'
import { useCurrentClient } from '~/composables/useCurrentClient'
import { CLIENT_NOT_FOUND_ERROR } from '~/utils/errors'

// Deep client routes (`/client/<uuid>/<page>`) are not covered by
// 3.redirect-general-routes.global.ts, so on a direct load / hard reload nothing
// awaits initializeCurrentClient() before the page fires its first `$gql` query.
// The result is an empty `client_data_endpoint` and a request to
// `https://.sec-auditor.com/v1/graphql`. Blocking here guarantees the current
// client (and its data endpoint) is ready before any client page renders.
export default defineNuxtRouteMiddleware(async (to) => {
  if (process.server) {
    return
  }

  const clientId = to.params.clientId
  if (!clientId || typeof clientId !== 'string') {
    return
  }

  // Auth + Keycloak redirect is handled by 2.route-protection.global.ts; without a
  // session, skip so we don't fire a GraphQL call before that redirect happens.
  const { status } = useAuth()
  if (status.value !== 'authenticated') {
    return
  }

  // Idempotent: returns without a network call when the client is already loaded
  // (isClientGood cache), so in-app navigation is not slowed down.
  const { initializeCurrentClient } = useCurrentClient()
  const result = await initializeCurrentClient(clientId)

  // unknown client: show the 404 error page instead of rendering a broken client page.
  // Returning the fatal error (instead of abortNavigation(err), which throws) makes the router
  // abort the navigation and call showError exactly once.
  if (result === 'not-found') {
    return createError(CLIENT_NOT_FOUND_ERROR)
  }
})
